import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import FlightSearchComponent from "views/UserInterface/FlightSearchComponent/FlightSearchComponent";
import { beatch } from "Utils/Images";

export default function PopularFlightRoutesContentPage() {
  const [round, setRound] = useState(true);
  const [oneWay, setOneWay] = useState(false);
  const [multiCity, setMultiCity] = useState(false);
  const location = useLocation();
  const { from, to } = useParams();
  const [activeTab, setActiveTab] = useState("overview");

  // Guard: if no content was passed
  if (!location.state || !location.state.content) {
    return (
      <section className="p-8">
        <h2 className="text-xl font-semibold text-red-600">
          Oops! No flight data available.
        </h2>
        <p className="mt-2">
          It looks like you arrived here without the necessary route data.
          Please go back and try selecting a route again.
        </p>
      </section>
    );
  }

  const { title, content } = location?.state;
  const originCity = title.split(" ")[0]; // e.g. "Toronto"
  const destinationCity =
    to.charAt(0).toUpperCase() + to.slice(1).toLowerCase(); // e.g. "Amritsar"

  // Destination-specific stats
  const avgDuration = content.averageFlightDuration?.[destinationCity] || "N/A";
  const priceRange = content.priceRangeUSD?.[destinationCity] || "N/A";
  const weeklyFlights = content.flightsPerWeek?.[destinationCity] || "N/A";

  // Attractions arrays
  const originAttractions = content.topAttractions?.[originCity] || [];
  const destinationAttractions =
    content.topAttractions?.[destinationCity] || [];

  // First image for hero fallback
  const heroImage =
    Array.isArray(content.images) && content.images.length > 0
      ? content.images[0]
      : null;

  // Utility to conditionally render tab content panels
  const renderTabPanel = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-6 px-4 py-6 text-gray-900">
            <h2 className="text-2xl font-semibold">Flight Overview</h2>
            <p className="leading-relaxed ">{content.description}</p>
            <div>
              <h3 className="text-lg font-medium">Major Airlines</h3>
              <ul className="mt-3 flex flex-wrap gap-3">
                {content.airlines.map((airline, idx) => (
                  <li
                    key={idx}
                    className="inline-block rounded-full bg-indigo-50 px-4 py-1 text-sm font-medium text-indigo-700"
                  >
                    {airline}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );

      case "details":
        return (
          <div className="space-y-6 px-4 py-6 text-gray-900">
            <h2 className="text-2xl font-semibold">Key Flight Details</h2>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="font-medium">Average Duration:</span>
                <span className="text-gray-700">{avgDuration}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Price Range (USD):</span>
                <span className="text-gray-700">{priceRange}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Flights per Week:</span>
                <span className="text-gray-700">{weeklyFlights}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Best Time to Travel:</span>
                <span className="text-gray-700">
                  {content.bestTimeToTravel}
                </span>
              </div>
            </div>
          </div>
        );

      case "attractions":
        return (
          <div className="grid grid-cols-1 gap-8 px-4 py-6 text-gray-900 md:grid-cols-2">
            {/* Origin City Attractions */}
            <div className="rounded-lg bg-white p-6 shadow-lg">
              <h2 className="mb-4 text-2xl font-semibold text-indigo-600">
                {originCity} Highlights
              </h2>
              <ul className="list-inside list-disc space-y-2 text-gray-900">
                {originAttractions.length > 0 ? (
                  originAttractions.map((spot, idx) => (
                    <li key={idx}>{spot}</li>
                  ))
                ) : (
                  <li>No data available</li>
                )}
              </ul>
            </div>

            {/* Destination City Attractions */}
            <div className="rounded-lg bg-white p-6 text-gray-900 shadow-lg">
              <h2 className="mb-4 text-2xl font-semibold text-indigo-600">
                {destinationCity} Highlights
              </h2>
              <ul className="list-inside list-disc space-y-2 text-gray-900">
                {destinationAttractions.length > 0 ? (
                  destinationAttractions.map((spot, idx) => (
                    <li key={idx}>{spot}</li>
                  ))
                ) : (
                  <li>No data available</li>
                )}
              </ul>
            </div>
          </div>
        );

      case "tips":
        return (
          <div className="space-y-6 px-4 py-6 text-gray-900">
            <h2 className="text-2xl font-semibold">Traveler Tips</h2>
            <ul className="list-inside list-disc space-y-2 text-gray-900">
              {content.tips.map((tip, idx) => (
                <li key={idx}>{tip}</li>
              ))}
            </ul>
          </div>
        );

      case "gallery":
        return (
          <div className="px-4 py-6 text-gray-900">
            <h2 className="mb-4 text-2xl font-semibold">Gallery</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {content.images.map((imgUrl, idx) => (
                <div
                  key={idx}
                  className="aspect-w-16 aspect-h-9 overflow-hidden rounded-lg shadow-lg"
                >
                  <img
                    src={imgUrl}
                    alt={`${originCity} to ${destinationCity} pic ${idx + 1}`}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <ScrollToTopOnRouteChange />

      {/* ───────────── Hero Banner ───────────── */}
      <div
        className={`relative h-48 bg-gray-800 bg-cover bg-center sm:h-56`}
        style={{ backgroundImage: `url(${beatch})` }}
      >
        {/* Dark overlay so text stays legible */}
        <div className="bg-black absolute inset-0 bg-opacity-40"></div>

        {/* Centered title */}
        <div className="relative z-10 flex h-full w-full items-center justify-center px-4">
          <h1 className="text-center text-2xl font-extrabold text-white sm:text-3xl md:text-4xl">
            {originCity} → {destinationCity}
          </h1>
        </div>
      </div>

      {/* ───────────── Two-Column Layout: Search (left) + Tabs (right) ───────────── */}
      <div className="mx-auto flex max-w-8xl flex-col gap-12 px-4 py-8 lg:flex-row">
        {/* ─────── Left Column: Flight Search ─────── */}
        <aside className="w-full lg:w-1/3">
          <div className="sticky top-20">
            {/* So the search box stays in view on large screens */}
            <FlightSearchComponent
              round={round}
              oneWay={oneWay}
              multiCity={multiCity}
              setRound={setRound}
              setOneWay={setOneWay}
              setMultiCity={setMultiCity}
            />
          </div>
        </aside>

        {/* ─────── Right Column: Tabs & Content ─────── */}
        <main className="w-full space-y-6 lg:w-2/3">
          {/* ───────────── Tab Bar ───────────── */}
          <div className="border-b border-gray-200 bg-[#001c49] ">
            <nav>
              <ul className="flex overflow-x-auto px-2">
                {[
                  { key: "overview", label: "Overview" },
                  { key: "details", label: "Details" },
                  { key: "attractions", label: "Attractions" },
                  { key: "tips", label: "Tips" },
                  { key: "gallery", label: "Gallery" },
                ].map((tab) => (
                  <li key={tab.key} className="mr-6">
                    <button
                      onClick={() => setActiveTab(tab.key)}
                      className={`whitespace-nowrap border-b-2 pb-2 text-sm font-medium transition-colors ${
                        activeTab === tab.key
                          ? "border-indigo-600 text-indigo-600"
                          : "border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800"
                      }`}
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* ───────────── Tab Content Panel ───────────── */}
          <section className="bg-gray-50">{renderTabPanel()}</section>
        </main>
      </div>
    </>
  );
}
